<script setup lang="ts">
import type { Magazine } from '@/models/content'
import { useGoogleAnalyticsPageView } from '~/composables/google-analytics'
import { useOpenPDFViewer } from '~~/composables/open-pdf-viewer'
import { useMembershipStore } from '@/stores/membership'
import { logError } from '~/utils/log-error'

const { callToAction, isDownloading, downloadProgress, cancelDownload } =
  useDownloadFile()
const membershipStore = useMembershipStore()
const { $auth, $cloudinary, $restClient, $seoManager } = useNuxtApp()
const { openPDFViewer } = useOpenPDFViewer()

const magazines = ref<Magazine[]>()
const firstMagazine = ref<Magazine>()
const loading = ref(true)
const isAuthorized = computed(
  () =>
    membershipStore.isSubscriberActive || membershipStore.hasOnlyTrialActive,
)
const seo = $seoManager.generateSeoInfo({
  title: 'Magazine',
})
useHead(seo)
useGoogleAnalyticsPageView(seo.title)

const fetchContent = async () => {
  try {
    const { data } = await $restClient.getMagazines()
    magazines.value = data
    firstMagazine.value = magazines.value.shift()
  } catch (error) {
    logError(error)
  }

  loading.value = false
}

function download() {
  callToAction.value.callback(
    firstMagazine.value.id,
    'magazine',
    firstMagazine.value.fileName,
    firstMagazine.value.subscriptionRequired,
  )
}

onMounted(fetchContent)
</script>

<template>
  <div class="flex grow flex-col justify-center">
    <LoadingSpinner
      v-if="$auth.store.token === undefined || loading"
      wrap-screen-with-submenu
    />

    <ClientOnly v-else>
      <div v-if="!loading" class="pb-20 lg:pb-36">
        <header class="relative bg-s-800 px-5 py-6 text-white lg:bg-s-900">
          <div
            class="mx-auto flex max-w-[820px] justify-between gap-4 md:items-center md:gap-20"
            data-testid="featured-magazine"
          >
            <div
              class="h-[136px] w-[100px] shrink-0 bg-s-800 md:h-[370px] md:w-[276px]"
            >
              <img
                v-if="firstMagazine"
                width="270"
                height="370"
                :src="
                  $cloudinary.resize(firstMagazine.coverUrl, 'magazine-hero')
                "
                alt=""
                class="h-full w-full object-cover object-center"
              />
            </div>

            <div v-if="firstMagazine" class="grow">
              <div>
                <p
                  class="text-base font-semibold"
                  data-testid="magazine-card-quarter"
                >
                  {{ firstMagazine.quarter }}
                </p>
                <p class="text-xs">
                  {{ firstMagazine.issue }}
                </p>
              </div>
              <h1
                class="mt-2 text-xl font-bold"
                data-testid="magazine-card-title"
              >
                {{ firstMagazine.title }}
              </h1>
              <div
                class="mt-2 text-sm"
                data-testid="magazine-card-summary"
                v-html="firstMagazine.summary"
              ></div>
              <div
                v-if="firstMagazine.tags.length"
                class="mt-4 flex flex-wrap gap-2"
              >
                <LinkedPill
                  v-for="tag in firstMagazine.tags"
                  :key="tag.id"
                  :slug="tag.slug"
                >
                  {{ tag.name }}
                </LinkedPill>
              </div>
              <div
                class="relative mt-6 flex h-8 max-h-[32px] flex-row items-center justify-end gap-2 md:mt-12 md:h-auto"
              >
                <BaseButton
                  v-if="firstMagazine.linkFlipHTML5 && isAuthorized"
                  variant="primary"
                  class="grow border-2 border-p-500 md:w-[110px] md:grow-0"
                  data-testid="view-pdf"
                  @click="
                    openPDFViewer(
                      firstMagazine.id,
                      firstMagazine.title,
                      firstMagazine.linkFlipHTML5,
                      'magazine',
                      firstMagazine.subscriptionRequired,
                    )
                  "
                >
                  View
                </BaseButton>
                <div
                  class="flex w-auto items-center justify-center md:w-[110px] md:p-0"
                >
                  <CircularProgressBar
                    v-if="isDownloading || downloadProgress"
                    :progress="downloadProgress"
                    :cancel-call-back="cancelDownload"
                    :is-loading="isDownloading"
                    @click="download"
                  />
                  <BaseButton
                    v-else
                    variant="tertiary"
                    class="grow !text-white hover:!text-black md:w-[110px] md:grow-0"
                    data-testid="call-to-action"
                    @click="download"
                  >
                    {{ callToAction.text }}
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
        </header>

        <section
          class="mx-auto max-w-content-container items-start gap-5 px-5 lg:mt-10 lg:flex"
        >
          <div class="mb-7 flex items-center gap-4 md:basis-1/4">
            <h2 class="hidden text-2xl font-bold lg:block">Previous Issues</h2>
            <hr class="hidden h-line-4 grow bg-s-900 lg:block" />
          </div>

          <div class="flex flex-col gap-5 lg:mr-1/12 lg:w-8/12">
            <MagazineCard
              v-for="magazine in magazines"
              :id="magazine.id"
              :key="magazine.id"
              :title="magazine.title"
              :summary="magazine.summary"
              :issue="magazine.issue"
              :quarter="magazine.quarter"
              :pills="magazine.tags"
              :cover-url="magazine.coverUrl"
              :file-name="magazine.fileName"
              :link-flip-html5="magazine.linkFlipHTML5"
              :subscription-required="magazine.subscriptionRequired"
            />
          </div>
        </section>
      </div>
    </ClientOnly>
  </div>
</template>

<style lang="css" scoped>
:deep(html),
:deep(body) {
  overflow: auto !important;
}
</style>
